'use client';

import { Editor } from '@tinymce/tinymce-react';
import { toast } from 'sonner';

import { useRef } from 'react';

import { uploadAttachment } from '@/core/lib/api/account/upload-attachment';
import { cn } from '@/core/lib/utils';
import { getCurrentTheme } from '@/core/config/theme-config';

// فونت فارسی هر برند برای تزریق به iframe ادیتور - TinyMCE محتوا رو تو یه
// iframe جدا رندر می‌کنه که فونت صفحه‌ی اصلی (تزریق‌شده توسط next/font) رو به
// ارث نمی‌بره، برای همین بدون این @font-face متن با فونت پیش‌فرض مرورگر
// (معمولاً یه serif نچسب) نمایش داده می‌شد.
const EDITOR_FONT_FILES: Record<string, { regular: string; bold: string }> = {
  tolica: { regular: 'YekanBakhFaNum-Light.woff2', bold: 'YekanBakhFaNum-SemiBold.woff2' },
  tolicav2: { regular: 'YekanBakhFaNum-Light.woff2', bold: 'YekanBakhFaNum-SemiBold.woff2' },
  choopar: { regular: 'Shabnam-Light-FD.woff2', bold: 'Shabnam-Bold-FD.woff2' },
  default: { regular: 'IRANYekanXFaNum-Light.woff2', bold: 'IRANYekanXFaNum-DemiBold.woff2' },
};

interface MyEditorProps {
  value?: string | null;
  onChange?: (content: string) => void;
  error?: string;
  /** حالت ساده و کم‌ارتفاع (مثل ادیتور متن نامه) - منوبار و ابزارهای پیشرفته حذف می‌شن */
  minimal?: boolean;
}

export default function EditorPage({ value, onChange, error, minimal = false }: MyEditorProps) {
  const _editorRef = useRef(null);
  const editorFont = EDITOR_FONT_FILES[getCurrentTheme()] || EDITOR_FONT_FILES.default;

  const handleUpload = async (file: File): Promise<string> => {
    try {
      const result = await uploadAttachment(file);

      if (result?.status === 'success' && result?.data?.url) {
        toast.success(result.message || 'تصویر با موفقیت آپلود شد');
        return result.data.url;
      } else {
        throw new Error('خطا در دریافت URL تصویر');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'خطا در آپلود تصویر';
      toast.error(msg);
      throw new Error(msg);
    }
  };

  return (
    <>
      {minimal && (
        <style>{`
          .letters-mini-editor .tox-tbtn {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
          }
          .letters-mini-editor .tox-tbtn .tox-icon {
            transform: scale(0.72);
          }
        `}</style>
      )}
      <div
        className={cn(
          'w-full bg-primary-0 rounded-md border',
          error ? 'border-red-600' : 'border-primary-300',
          minimal && 'letters-mini-editor'
        )}
      >
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          value={value ?? ''}
          onEditorChange={onChange}
          init={{
            width: '100%',
            // @ts-expect-error - TinyMCE license_key property is not in types
            license_key: 'gpl',
            height: minimal ? 170 : 400,
            directionality: 'rtl',
            menubar: minimal ? false : 'file edit view insert format tools table help',
            toolbar_location: minimal ? 'bottom' : 'top',
            statusbar: !minimal,
            plugins: minimal
              ? ['link', 'lists', 'autolink']
              : [
                  'code',
                  'image',
                  'imagetools', // 👈 ابزارهای ادیت تصویر
                  'link',
                  'table',
                  'lists',
                  'wordcount',
                  'advlist',
                  'autolink',
                  'preview',
                  'anchor',
                  'searchreplace',
                  'visualblocks',
                  'fullscreen',
                  'insertdatetime',
                  'media',
                  'charmap',
                ],
            toolbar: minimal
              ? 'bold italic underline | bullist numlist | link'
              : `
              undo redo | styleselect | bold italic underline forecolor backcolor |
              fontsizeselect fontselect lineheight |
              alignleft aligncenter alignright alignjustify |
              bullist numlist outdent indent | link image table | code
            `,
            fontsize_formats:
              '10px 12px 14px 16px 18px 20px 24px 28px 32px 36px 48px',
            font_family_formats: `
              Arial=arial,helvetica,sans-serif;
              Times New Roman=times new roman,times;
              Tahoma=tahoma,arial,helvetica,sans-serif;
              IranSans=IranSans, Arial, sans-serif;
            `,
            lineheight_formats: '1 1.2 1.5 1.75 2 2.5 3',
            content_style: `
              @font-face {
                font-family: 'EditorBrandFont';
                src: url('/fonts/${editorFont.regular}') format('woff2');
                font-weight: 400;
                font-display: swap;
              }
              @font-face {
                font-family: 'EditorBrandFont';
                src: url('/fonts/${editorFont.bold}') format('woff2');
                font-weight: 700;
                font-display: swap;
              }
              body {
                font-family: 'EditorBrandFont', Tahoma, sans-serif;
                font-size: 14px;
                line-height: 1.8;
                direction: rtl;
                text-align: right;
              }
            `,
            // 👇 استایل‌های آماده برای عکس
            style_formats: [
              {
                title: 'تصویر گرد (Circle)',
                selector: 'img',
                styles: { borderRadius: '100%' },
              },
              {
                title: 'تصویر با گوشه گرد',
                selector: 'img',
                styles: { borderRadius: '12px' },
              },
              {
                title: 'تصویر سایه‌دار',
                selector: 'img',
                styles: {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                },
              },
            ],
            image_dimensions: true,
            image_caption: true,
            imagetools_toolbar:
              'rotateleft rotateright | flipv fliph | editimage imageoptions',
            // @ts-expect-error - TinyMCE images_upload_handler property is not in types
            images_upload_handler: async blobInfo => {
              // blobInfo.blob() یه Blob ساده‌ست، نه File - فاقد name/extension.
              // بدون این تبدیل، FormData اسمش رو "blob" (بدون پسوند) می‌ذاره
              // و اعتبارسنجی پسوند فایل سمت بک‌اند رد می‌شه.
              const blob = blobInfo.blob();
              const file = new File([blob], blobInfo.filename(), { type: blob.type });
              const url = await handleUpload(file);
              return url;
            },
          }}
        />
      </div>
      {error && <p className="text-red-600 mt-2">{error}</p>}
    </>
  );
}
